const SUPPORTED_LANGUAGES = new Set([
  "js",
  "javascript",
  "ts",
  "typescript",
  "tsx",
  "jsx",
  "css",
  "html",
  "json",
  "yaml",
  "yml",
  "python",
  "py",
  "sql",
  "bash",
  "shell",
  "sh",
  "xml",
  "markdown",
  "md",
  "text",
  "plaintext",
  "mermaid",    // mermaid is handled separately in the UI, but kept valid
  "",           // empty = plain text
]);

// ─── Regex ────────────────────────────────────────────────────────────────────
// Matches the opening line of any fenced code block:  ```<language>
// Captures the language token (group 1).
const FENCED_CODE_OPEN_RE = /^(```)\s*([^\s`]*)\s*$/gm;

/**
 * Replaces unsupported fenced-code-block language identifiers with `text`
 * so MDXEditor can always parse the content without errors.
 *
 * Mermaid blocks are intentionally left untouched — they are extracted
 * and rendered separately by SbPACDocumentContentV2.
 *
 * @param {string} markdown  Raw markdown string from the API.
 * @returns {string}         Sanitized markdown safe for MDXEditor.
 */
export const sanitizeCodeBlockLanguages = (markdown) => {
  if (!markdown) return markdown;

  return markdown.replace(FENCED_CODE_OPEN_RE, (_match, fences, lang) => {
    const normalized = (lang || "").trim().toLowerCase();

    // Already supported — pass through unchanged.
    if (SUPPORTED_LANGUAGES.has(normalized)) {
      return `${fences}${lang}`;
    }

    // Unknown language → replace with "text" (plain code block).
    return `${fences}text`;
  });
};

// ─── Robust Table Normalization ───────────────────────────────────────────────
//
// AI-generated PRD markdown produces tables in wildly inconsistent formats:
//   - Missing separator rows
//   - Missing leading or trailing pipes
//   - Inconsistent column counts across rows
//   - Separator rows with too few dashes
//   - Tables embedded after mermaid blocks
//
// This normalizer detects ALL table variants and converts them into strict
// GFM format that MDXEditor's tablePlugin can always parse.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Checks if a line looks like a fenced code block delimiter (``` or ~~~).
 */
const isCodeFence = (line) => {
  const trimmed = line.trim();
  return trimmed.startsWith("```") || trimmed.startsWith("~~~");
};

/**
 * Flexible table row detection.
 *
 * A line is considered a table row if it contains at least 2 pipe characters
 * and isn't inside a code block. This catches all variants:
 *   - `| Step | Action | Agent |`     (standard)
 *   - `| Step | Action | Agent`       (no trailing pipe)
 *   - `Step | Action | Agent |`       (no leading pipe)
 *   - `Step | Action | Agent`         (no pipes on either side)
 *
 * We exclude lines that are ONLY pipes/dashes (separators are handled separately).
 */
const looksLikeTableRow = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return false;

  // Count the pipe characters (excluding escaped pipes \|)
  const pipeCount = (trimmed.match(/(?<!\\)\|/g) || []).length;

  // Need at least 2 pipes to form a table (e.g., `col1 | col2`)
  return pipeCount >= 2;
};

/**
 * Checks if a line is a GFM separator row.
 * Matches: `| --- | --- |`, `| :--- | :---: |`, `--- | ---`, etc.
 */
const isSeparatorRow = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return false;

  // Split by pipe, remove empty leading/trailing segments
  const rawCells = trimmed.split("|");
  const cells = rawCells.filter((cell, idx) => {
    // Skip empty first/last segments (from leading/trailing pipes)
    if (idx === 0 && cell.trim() === "") return false;
    if (idx === rawCells.length - 1 && cell.trim() === "") return false;
    return true;
  });

  if (cells.length === 0) return false;

  // Every cell must match the separator pattern: optional colon, 1+ dashes, optional colon
  return cells.every((cell) => /^\s*:?-+:?\s*$/.test(cell));
};

/**
 * Splits a table row into cells.
 * Handles rows with or without leading/trailing pipes.
 */
const splitRowCells = (line) => {
  const trimmed = line.trim();
  const parts = trimmed.split("|");

  // Remove empty first element if line starts with |
  if (parts.length > 0 && parts[0].trim() === "") {
    parts.shift();
  }
  // Remove empty last element if line ends with |
  if (parts.length > 0 && parts[parts.length - 1].trim() === "") {
    parts.pop();
  }

  return parts.map((cell) => cell.trim());
};

/**
 * Rebuilds a table row with proper leading/trailing pipes and consistent formatting.
 */
const buildNormalizedRow = (cells) => {
  return `| ${cells.join(" | ")} |`;
};

/**
 * Builds a proper GFM separator row with :--- alignment markers.
 * Uses :--- format because MDXEditor's tablePlugin renders it most reliably.
 */
const buildSeparatorRow = (colCount) => {
  const cells = Array.from({ length: colCount }, () => ":---");
  return `| ${cells.join(" | ")} |`;
};

/**
 * Pads or trims cell arrays to match the expected column count.
 * - If too few cells: adds empty cells at the end
 * - If too many cells: trims extra cells from the end
 */
const normalizeCellCount = (cells, expectedCount) => {
  if (cells.length === expectedCount) return cells;

  if (cells.length < expectedCount) {
    // Pad with empty cells
    return [...cells, ...Array(expectedCount - cells.length).fill("")];
  }

  // Trim extra cells
  return cells.slice(0, expectedCount);
};

/**
 * Normalizes all markdown tables in the document to strict GFM format.
 *
 * This is the main function. It:
 * 1. Tracks code block state (skips tables inside code fences)
 * 2. Flexibly detects table rows (with/without leading/trailing pipes)
 * 3. Normalizes all rows to have proper `| cell | cell |` format
 * 4. Ensures a valid separator row exists with `:---` alignment markers
 * 5. Pads/trims rows to match the header column count
 * 6. Adds blank lines around tables for clean MDXEditor parsing
 *
 * @param {string} markdown  Raw markdown string.
 * @returns {string}         Markdown with all tables in strict GFM format.
 */
export const normalizeMarkdownTables = (markdown) => {
  if (!markdown) return markdown;

  const lines = markdown.split("\n");
  const result = [];
  let i = 0;
  let insideCodeBlock = false;

  while (i < lines.length) {
    const line = lines[i];

    // Track code block boundaries — never modify table-like content inside code blocks
    if (isCodeFence(line)) {
      insideCodeBlock = !insideCodeBlock;
      result.push(line);
      i++;
      continue;
    }

    if (insideCodeBlock) {
      result.push(line);
      i++;
      continue;
    }

    // Check if this line looks like a table row
    if (looksLikeTableRow(line) && !isSeparatorRow(line)) {
      // Collect all consecutive table-like lines (rows + separators)
      const tableLines = [line];
      i++;

      while (i < lines.length) {
        const nextLine = lines[i];
        if (looksLikeTableRow(nextLine) || isSeparatorRow(nextLine)) {
          tableLines.push(nextLine);
          i++;
        } else {
          break;
        }
      }

      // Only treat as a table if we have at least 2 lines (header + data or separator)
      if (tableLines.length >= 2) {
        // Parse all rows into cell arrays
        const parsedRows = tableLines.map((row) => ({
          cells: splitRowCells(row),
          isSep: isSeparatorRow(row),
        }));

        // Determine the column count from the header (first non-separator row)
        const headerRow = parsedRows.find((r) => !r.isSep);
        const colCount = headerRow ? headerRow.cells.length : parsedRows[0].cells.length;

        // Check if there's already a valid separator at position 1
        const hasSeparator = parsedRows.length >= 2 && parsedRows[1].isSep;

        // Ensure a blank line before the table for clean parsing
        if (result.length > 0 && result[result.length - 1].trim() !== "") {
          result.push("");
        }

        // Build the normalized table
        if (hasSeparator) {
          // Already has a separator — just normalize all rows
          parsedRows.forEach((row) => {
            if (row.isSep) {
              result.push(buildSeparatorRow(colCount));
            } else {
              const normalizedCells = normalizeCellCount(row.cells, colCount);
              result.push(buildNormalizedRow(normalizedCells));
            }
          });
        } else {
          // Missing separator — insert one after the header
          const headerCells = normalizeCellCount(parsedRows[0].cells, colCount);
          result.push(buildNormalizedRow(headerCells));
          result.push(buildSeparatorRow(colCount));

          // Add remaining rows (skip any misplaced separators)
          for (let j = 1; j < parsedRows.length; j++) {
            if (parsedRows[j].isSep) {
              // Skip duplicate/misplaced separator rows
              continue;
            }
            const normalizedCells = normalizeCellCount(parsedRows[j].cells, colCount);
            result.push(buildNormalizedRow(normalizedCells));
          }
        }

        // Ensure a blank line after the table
        if (i < lines.length && lines[i].trim() !== "") {
          result.push("");
        }
      } else {
        // Single table-like line — not a real table, pass through
        result.push(...tableLines);
      }
    } else {
      result.push(line);
      i++;
    }
  }

  return result.join("\n");
};

const sanitizeMarkdown = (markdown) => {
  if (!markdown) return markdown;

  let result = markdown;

  // 1. Normalize code-block languages.
  result = sanitizeCodeBlockLanguages(result);
  result = normalizeMarkdownTables(result);

  return result;
};

export default sanitizeMarkdown;